import { h, Fragment, ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";

import classname from "../../../../foundation/utils/classname";
import FilterInput, { FilterInputProps } from "../../Input/FilterInput";
import { ButtonProps, ButtonVariant } from "../../Button";
import InformationView from "../../InformationView";
import DropdownMenu, { DropdownMenuProps } from "../DropdownMenu";

import styles from "./FilterDropdownMenu.module.css";

interface Props<T> {
    class?: string;
    buttonProps?: DropdownMenuProps["buttonProps"];
    menuProps?: DropdownMenuProps["menuProps"];
    inputProps?: FilterInputProps;
    onMenuStateChange?: (open: boolean) => void;
    loading?: { description: string; };
    error?: { description: string; onRetry?: () => void; };
    items: T[];
    renderItems: (filteredItems: T[], query: string) => ComponentChildren;
    renderNoResultsDescription: (query: string) => string;
    filterItem?: (item: T, query: string) => boolean;
    filterItemByScore?: (item: T, query: string) => number;
    onItemClick?: (value: string, filteredItems: T[]) => void;
}

export default function FilterDropdownMenu<T>({
    class: cssClass,
    buttonProps = {},
    menuProps = {},
    inputProps = {},
    loading,
    error,
    items,
    renderItems,
    renderNoResultsDescription,
    filterItem,
    filterItemByScore,
    onItemClick,
    onMenuStateChange
}: Props<T>) {
    const [inputValue, setInputValue] = useState("");
    const [filteredItems, setFilteredItems] = useState(items);
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (!query.trim()) {
            setFilteredItems(items);

            return;
        }

        if (filterItemByScore) {
            const newFilteredItems = items
                .map(item => ({
                    item,
                    score: filterItemByScore(item, query)
                }))
                .filter(({ score }) => score > 0)
                .sort((a, b) => b.score - a.score)
                .map(({ item }) => item);

            setFilteredItems(newFilteredItems);
        } else if (filterItem) {
            const newFilteredItems = items.filter(item => filterItem(item, query));

            setFilteredItems(newFilteredItems);
        } else {
            throw new Error("`FilterDropdownMenu` requires either `filterItem` or `filterItemByScore` prop to filter items");
        }
    }, [items, query]);

    function handleInput(event: Event) {
        const input = event.target as HTMLInputElement;
        setInputValue(input.value);
    }

    function handleFilter(value: string) {
        setQuery(value);
    }

    function handleMenuItemClick(_: Event, value?: string) {
        if (!value) {
            return;
        }

        onItemClick?.(value, filteredItems);
    }

    function handleResetSearch() {
        setInputValue("");
        setQuery("");
    }

    function renderInformationView(
        description: string,
        informationButtonProps?: Required<Pick<ButtonProps, "children" | "onClick">>
    ) {
        return (
            <InformationView
                class={styles.informationView}
                description={description}
                secondaryButtonProps={informationButtonProps ? {
                    variant: ButtonVariant.Ninja,
                    ...informationButtonProps
                } : undefined}/>
        );
    }

    function renderContents() {
        if (loading) {
            return renderInformationView(loading.description);
        }

        if (error) {
            return renderInformationView(error.description, error.onRetry ? {
                children: "Retry",
                onClick: error.onRetry
            } : undefined);
        }

        return (
            <>
                <div class={styles.inputContainer}>
                    <FilterInput
                        class={styles.input}
                        autoFocus
                        value={inputValue}
                        onFilter={handleFilter}
                        onInput={handleInput}
                        {...inputProps}/>
                </div>
                {filteredItems.length
                    ? renderItems(filteredItems, query)
                    : renderInformationView(renderNoResultsDescription(query), {
                        children: "Reset search",
                        onClick: handleResetSearch
                    })}
            </>
        );
    }

    const { class: menuClass, ...menuPropsRest } = menuProps;

    return (
        <DropdownMenu
            class={cssClass}
            buttonProps={buttonProps}
            menuProps={{
                class: classname(styles.filterDropdownMenu, menuClass),
                ignoredKeyDownEventKeys: {
                    arrowLeft: true,
                    arrowRight: true,
                    space: true
                },
                onItemClick: handleMenuItemClick,
                ...menuPropsRest
            }}
            onMenuStateChange={onMenuStateChange}>
            {renderContents()}
        </DropdownMenu>
    );
}
